import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  RestoreFromTrash as RestoreIcon,
} from '@mui/icons-material';
import { Devis, DevisStatus } from '../../types/devis';
import { User } from '../../types/auth';
import { Client } from '../../types/client';
import { devisAPI, userAPI, clientsAPI, auditLogsAPI } from '../../services/api';
import ColumnFilter from '../../components/ColumnFilter';
import { useColumnFilters } from '../../hooks/useColumnFilters';
import AlertDialog from '../../components/AlertDialog';
import useAlert from '../../hooks/useAlert';
import { getModuleTableTheme } from '../../theme/tableTheme';
import CustomTablePagination from '../../components/CustomTablePagination';
import PermissionButton from '../../components/PermissionButton';
import { extractErrorMessage } from '../../utils/errorHandler';
import usePdfPreview from '../../hooks/usePdfPreview';
import DevisPreview from './DevisPreview';
import DevisFormDialog from './DevisFormDialog';
// generateDevisPDF is dynamically imported in handleConfirmPrint so the
// @react-pdf/renderer chunk only loads when the user clicks Print.
import TruncatedTextWithTooltip from '../../components/TruncatedTextWithTooltip';
import { FilterListOff as FilterListOffIcon } from '@mui/icons-material';

const COMMERCIAL_COLOR = '#4CAF50';

const DEVIS_FILTER_SCHEMA = {
  date:        { type: 'dateRange',    paramFrom: 'date_from', paramTo: 'date_to' },
  reference:   { type: 'text',         param: 'reference' },
  client_id:   { type: 'autocomplete', param: 'client_id', transform: (v: string) => Number(v) },
  designation: { type: 'text',         param: 'designation' },
  montant:     { type: 'numberRange',  paramMin: 'montant_min', paramMax: 'montant_max' },
  status:      { type: 'select',       param: 'status' },
  user_id:     { type: 'autocomplete', param: 'user_id', transform: (v: string) => Number(v) },
} as const;

const DevisList: React.FC = () => {
  const [devisList, setDevisList] = useState<Devis[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deletedDevisList, setDeletedDevisList] = useState<Devis[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [previewDevis, setPreviewDevis] = useState<Devis | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [editDevisId, setEditDevisId] = useState<number | undefined>(undefined);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printDevis, setPrintDevis] = useState<Devis | null>(null);
  const [includeCommercialName, setIncludeCommercialName] = useState(false);
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();

  // Column filters
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(
    DEVIS_FILTER_SCHEMA,
    { onChange: () => setPage(0) }
  );

  // Fetch users + clients for filter dropdowns
  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          userAPI.getUsers(0, 1000),
          clientsAPI.getAllForDropdown(),
        ]);
        setUsers(u);
        setClients(c);
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    })();
  }, []);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchDevis = useCallback(async () => {
    try {
      setLoading(true);
      const skip = page * rowsPerPage;
      // status, client_id, user_id go positionally; everything else into the extras bag
      const { status, client_id, user_id, ...extra } = toQueryParams();
      const response = await devisAPI.getPaginated(
        skip,
        rowsPerPage,
        debouncedSearch || undefined,
        (status as string) || undefined,
        (client_id as number | undefined),
        (user_id as number | undefined),
        Object.keys(extra).length > 0 ? extra : undefined
      );
      setDevisList(response.items);
      setTotalCount(response.total);
    } catch (err) {
      showError('Erreur lors du chargement des devis');
      console.error('Error fetching devis:', err);
      setDevisList([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const fetchDeletedDevis = async () => {
    try {
      setLoading(true);
      const data = await devisAPI.getDeleted();
      setDeletedDevisList(data);
    } catch (err) {
      showError('Erreur lors du chargement des devis supprimés');
      console.error('Error fetching deleted devis:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedDevis();
    }
  };

  const handleRestore = async (devis: Devis) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer le devis "${devis.reference}" ?`,
      async () => {
        try {
          await devisAPI.restore(devis.id);
          showSuccess('Devis restauré avec succès');
          fetchDeletedDevis();
          fetchDevis();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler',
      }
    );
  };

  useEffect(() => {
    if (tabValue === 0) {
      fetchDevis();
    }
  }, [fetchDevis, tabValue]);


  const handleDelete = async (devis: Devis) => {
    try {
      await devisAPI.delete(devis.id);
      showSuccess('Devis supprimé avec succès');
      fetchDevis();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const handleOpenPrintDialog = (devis: Devis) => {
    setPrintDevis(devis);
    setIncludeCommercialName(false);
    setPrintDialogOpen(true);
  };

  const handleClosePrintDialog = () => {
    setPrintDialogOpen(false);
    setPrintDevis(null);
  };

  const handleConfirmPrint = () => {
    if (printDevis) {
      const devis = printDevis;
      const options = { includeCommercialName };
      // Open the PDF preview; the user prints/downloads from the viewer toolbar.
      openPreview({
        getBlob: async () => {
          const { getDevisPDFBlob } = await import('../../utils/devisPdfReactPdf');
          return getDevisPDFBlob(devis, options);
        },
        fileName: `Devis_${devis.reference}.pdf`,
      });
      auditLogsAPI.logPdfDownload('Devis', devis.id, `Aperçu PDF - ${devis.reference}`);
    }
    handleClosePrintDialog();
  };

  const handleOpenNewDevis = () => {
    setEditDevisId(undefined);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleOpenEditDevis = (devisId: number) => {
    setEditDevisId(devisId);
    setFormMinimized(false);
    setFormDialogOpen(true);
  };

  const handleCloseFormDialog = () => {
    setFormDialogOpen(false);
    setFormMinimized(false);
    setEditDevisId(undefined);
  };

  const handleFormSuccess = () => {
    fetchDevis();
  };

  const getStatusChip = (status: string) => {
    switch (status) {
      case DevisStatus.EN_COURS:
        return <Chip label="En cours" color="warning" size="small" />;
      case DevisStatus.VALIDE:
        return <Chip label="Validé" color="success" size="small" />;
      case DevisStatus.ANNULE:
        return <Chip label="Annulé" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 'none', py: 3, px: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestion des Devis</Typography>
        {tabValue === 0 && (
        <PermissionButton
          moduleName="Commercial"
          operation="add"
          miniModuleName="Devis"
          variant="contained"
          onClick={handleOpenNewDevis}
          startIcon={<AddIcon />}
          sx={{
            backgroundColor: '#4CAF50',
            '&:hover': {
              backgroundColor: '#388E3C',
            },
          }}
        >
          Nouveau Devis
        </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Devis Actifs (${totalCount})`} />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
      <>
      {/* Filters */}
      <Box display="flex" gap={2} mb={3} alignItems="center">
        <TextField
          placeholder="Rechercher par référence ou client..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 350 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {hasActive() && (
          <Button
            size="small"
            startIcon={<FilterListOffIcon />}
            onClick={reset}
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            Effacer les filtres
          </Button>
        )}
      </Box>

      <TableContainer
        component={Paper}
        sx={[
          getModuleTableTheme('commercial'),
          {
            // No internal maxHeight: the table grows with its content and the
            // page scrolls instead of a nested vertical scrollbar.
            // overflow: visible removes the scroll context so the sticky
            // row-identity cells anchor to the viewport and follow the page
            // scroll (tableLayout: fixed below prevents any horizontal spill).
            overflow: 'visible',
            // Tighten cell padding to reclaim the wasted horizontal space so the
            // table fits the viewport width without horizontal scrolling.
            '& .MuiTableCell-root': {
              padding: '8px 10px !important',
              '&:first-of-type': { paddingLeft: '16px !important' },
              '&:last-child': { paddingRight: '16px !important' },
            },
          },
        ] as any}
      >
        {/* tableLayout: fixed makes the table fill the container exactly and gives
            the slack to Désignation instead of overflowing into a scrollbar. */}
        <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '92px', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Date
                  <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={COMMERCIAL_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={{ width: '96px', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Référence
                  <ColumnFilter type="text" value={filters.reference} onChange={(v) => update('reference', v)} label="référence" color={COMMERCIAL_COLOR} />
                </Box>
              </TableCell>
              <TableCell sx={{ width: '150px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Client
                  <ColumnFilter
                    type="autocomplete"
                    value={filters.client_id}
                    onChange={(v) => update('client_id', v)}
                    label="Client"
                    color={COMMERCIAL_COLOR}
                    options={clients.map(c => ({ value: c.id.toString(), label: c.name }))}
                  />
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Désignation
                  <ColumnFilter type="text" value={filters.designation} onChange={(v) => update('designation', v)} label="désignation" color={COMMERCIAL_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="right" sx={{ width: '150px', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  Montant
                  <ColumnFilter type="numberRange" value={filters.montant} onChange={(v) => update('montant', v)} label="montant" color={COMMERCIAL_COLOR} />
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ width: '96px', whiteSpace: 'nowrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  Statut
                  <ColumnFilter
                    type="select"
                    value={filters.status}
                    onChange={(v) => update('status', v)}
                    label="statut"
                    color={COMMERCIAL_COLOR}
                    options={[
                      { value: DevisStatus.EN_COURS, label: 'En cours' },
                      { value: DevisStatus.VALIDE, label: 'Validé' },
                      { value: DevisStatus.ANNULE, label: 'Annulé' },
                    ]}
                  />
                </Box>
              </TableCell>
              <TableCell sx={{ width: '170px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Créé par
                  <ColumnFilter
                    type="autocomplete"
                    value={filters.user_id}
                    onChange={(v) => update('user_id', v)}
                    label="créé par"
                    color={COMMERCIAL_COLOR}
                    options={users.map(u => ({ value: u.id.toString(), label: u.full_name || u.email }))}
                  />
                </Box>
              </TableCell>
              <TableCell align="center" sx={{ width: '118px' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : devisList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Aucun devis trouvé
                </TableCell>
              </TableRow>
            ) : (
              devisList.map((devis) => {
                const itemCount = devis.items?.length || 1;
                // Long devis (>8 designation rows) get their identity cells "scroll-follow"
                // (sticky around mid-viewport). Short ones stay vertically centered & static.
                const isLong = itemCount > 8;
                const vAlign = isLong ? 'top' : 'middle';
                const stickySx = isLong ? { position: 'sticky', top: 'calc(50vh - 40px)' } : {};
                return devis.items && devis.items.length > 0 ? (
                  devis.items.map((item, index) => (
                    <TableRow key={`${devis.id}-${index}`}>
                      {index === 0 && (
                        <>
                          <TableCell rowSpan={itemCount} sx={{ verticalAlign: vAlign, whiteSpace: 'nowrap' }}>
                            <Box sx={stickySx}>
                              {new Date(devis.date).toLocaleDateString('fr-FR')}
                            </Box>
                          </TableCell>
                          <TableCell rowSpan={itemCount} sx={{ verticalAlign: vAlign, whiteSpace: 'nowrap' }}>
                            <Box sx={stickySx}>
                              {devis.reference}
                            </Box>
                          </TableCell>
                          <TableCell rowSpan={itemCount} sx={{ verticalAlign: vAlign }}>
                            <Box sx={stickySx}>
                              <TruncatedTextWithTooltip text={devis.client_name || '-'} maxWidth="100%" />
                            </Box>
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ borderLeft: '1px solid rgba(224, 224, 224, 1)' }}>
                        <TruncatedTextWithTooltip text={item.designation} maxWidth="100%" />
                      </TableCell>
                      {index === 0 && (
                        <>
                          <TableCell rowSpan={itemCount} align="right" sx={{ verticalAlign: vAlign, borderLeft: '1px solid rgba(224, 224, 224, 1)', whiteSpace: 'nowrap' }}>
                            <Box sx={{ ...stickySx, textAlign: 'right' }}>
                              {(Math.round(Number(devis.montant_total) * 1.2 * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH TTC
                            </Box>
                          </TableCell>
                          <TableCell rowSpan={itemCount} align="center" sx={{ verticalAlign: vAlign, whiteSpace: 'nowrap' }}>
                            <Box sx={{ ...stickySx, display: 'flex', justifyContent: 'center' }}>
                              {getStatusChip(devis.status)}
                            </Box>
                          </TableCell>
                          <TableCell rowSpan={itemCount} sx={{ verticalAlign: vAlign, whiteSpace: 'nowrap' }}>
                            <Box sx={{ ...stickySx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={devis.user_name || '-'}>
                              {devis.user_name || '-'}
                            </Box>
                          </TableCell>
                          <TableCell rowSpan={itemCount} align="center" sx={{ verticalAlign: vAlign, whiteSpace: 'nowrap' }}>
                            <Box sx={{ ...stickySx, display: 'grid', gridTemplateColumns: 'repeat(2, auto)', justifyItems: 'center', width: 'fit-content', mx: 'auto' }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenPrintDialog(devis)}
                              title="Imprimer"
                            >
                              <PrintIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => setPreviewDevis(devis)}
                              title="Aperçu"
                            >
                              <ViewIcon />
                            </IconButton>
                            <PermissionButton
                              moduleName="Commercial"
                              operation="edit"
                              miniModuleName="Devis"
                              component="IconButton"
                              onClick={() => handleOpenEditDevis(devis.id)}
                            >
                              <EditIcon />
                            </PermissionButton>
                            <PermissionButton
                              moduleName="Commercial"
                              operation="delete"
                              miniModuleName="Devis"
                              component="IconButton"
                              onClick={() => {
                                showConfirm(
                                  `Êtes-vous sûr de vouloir supprimer le devis "${devis.reference}" ?`,
                                  () => handleDelete(devis),
                                  {
                                    title: 'Confirmer la suppression',
                                    confirmText: 'Supprimer',
                                    cancelText: 'Annuler',
                                  }
                                );
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </PermissionButton>
                            </Box>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow key={devis.id}>
                    <TableCell>
                      {new Date(devis.date).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>{devis.reference}</TableCell>
                    <TableCell>
                      <TruncatedTextWithTooltip text={devis.client_name || '-'} maxWidth="100%" />
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {(Math.round(Number(devis.montant_total) * 1.2 * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH TTC
                    </TableCell>
                    <TableCell align="center">{getStatusChip(devis.status)}</TableCell>
                    <TableCell>{devis.user_name || '-'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenPrintDialog(devis)}
                        title="Imprimer"
                      >
                        <PrintIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setPreviewDevis(devis)}
                        title="Aperçu"
                      >
                        <ViewIcon />
                      </IconButton>
                      <PermissionButton
                        moduleName="Commercial"
                        operation="edit"
                        miniModuleName="Devis"
                        component="IconButton"
                        onClick={() => handleOpenEditDevis(devis.id)}
                      >
                        <EditIcon />
                      </PermissionButton>
                      <PermissionButton
                        moduleName="Commercial"
                        operation="delete"
                        miniModuleName="Devis"
                        component="IconButton"
                        onClick={() => {
                          showConfirm(
                            `Êtes-vous sûr de vouloir supprimer le devis "${devis.reference}" ?`,
                            () => handleDelete(devis),
                            {
                              title: 'Confirmer la suppression',
                              confirmText: 'Supprimer',
                              cancelText: 'Annuler',
                            }
                          );
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </PermissionButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {tabValue === 1 && (
      <>
      <TableContainer component={Paper} sx={[getModuleTableTheme('commercial'), { maxHeight: 'calc(100vh - 230px)' }] as any}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Référence</TableCell>
              <TableCell>Client</TableCell>
              <TableCell align="right">Montant</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell>Date de Suppression</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Chargement...
                </TableCell>
              </TableRow>
            ) : deletedDevisList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Aucun devis supprimé
                </TableCell>
              </TableRow>
            ) : (
              deletedDevisList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((devis) => (
                  <TableRow key={devis.id}>
                    <TableCell>
                      {new Date(devis.date).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>{devis.reference}</TableCell>
                    <TableCell>{devis.client_name}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {(Math.round(Number(devis.montant_total) * 1.2 * 100) / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH TTC
                    </TableCell>
                    <TableCell align="center">{getStatusChip(devis.status)}</TableCell>
                    <TableCell>
                      {devis.deleted_at
                        ? new Date(devis.deleted_at).toLocaleDateString('fr-FR')
                        : '-'}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleRestore(devis)}
                        color="primary"
                        title="Restaurer"
                      >
                        <RestoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        count={deletedDevisList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      </>
      )}

      {/* Preview Modal */}
      {previewDevis && (
        <DevisPreview
          devis={previewDevis}
          open={!!previewDevis}
          onClose={() => setPreviewDevis(null)}
          onPrint={() => handleOpenPrintDialog(previewDevis)}
          onEdit={() => {
            setPreviewDevis(null);
            handleOpenEditDevis(previewDevis.id);
          }}
        />
      )}

      {/* Form Dialog */}
      <DevisFormDialog
        open={formDialogOpen}
        onClose={handleCloseFormDialog}
        onSuccess={handleFormSuccess}
        devisId={editDevisId}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
      />

      {/* Print Options Dialog */}
      <Dialog open={printDialogOpen} onClose={handleClosePrintDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Options d'impression</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeCommercialName}
                onChange={(e) => setIncludeCommercialName(e.target.checked)}
              />
            }
            label="Afficher le nom du commercial"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePrintDialog}>Annuler</Button>
          <Button onClick={handleConfirmPrint} variant="contained" startIcon={<PrintIcon />}>
            Imprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default DevisList;
